import {
  IPracticeMigration,
  IDestinationEntity,
  IDestinationEntityRecord,
  FailedDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IGetRecordResponse,
  ITranslationMap,
  IDestinationEntityJobRunOptions,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Firestore,
  getError,
  HISTORY_DATE_FORMAT,
  initFirestoreModel,
  isINamedDocument,
  resolveSequentially,
  snapshotCombineLatest,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  WithPrimaryKey,
  WithRef,
  type INamedDocument,
} from '@principle-theorem/shared';
import {
  BasePatientAppointmentDestinationEntity,
  IPatientAppointmentBuildData,
  IPatientAppointmentJobData,
  IPatientAppointmentMigrationData,
  PATIENT_APPOINTMENT_RESOURCE_TYPE,
} from '../../../destination/entities/patient-appointments';
import { TranslationMapHandler } from '../../../translation-map';
import { DocumentReference } from '@google-cloud/firestore';
import {
  TreatmentStep,
  Event,
  TreatmentPlan,
  Interaction,
  stafferToNamedDoc,
  Brand,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  type IPatient,
  isTag,
  ITreatmentCategory,
  ITag,
  AppointmentStatus,
  TreatmentStepStatus,
  TreatmentPlanStatus,
  type IInteractionV2,
  InteractionType,
  type IPractice,
  type IStaffer,
  IAssociatedTreatment,
  EventType,
  type IEvent,
  ParticipantType,
} from '@principle-theorem/principle-core/interfaces';
import { compact, uniqBy } from 'lodash';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  findTreatmentStepForAppointment,
  getDefaultTreatmentPlanName,
  IStepWithIdentifiers,
} from '../../../destination/entities/patient-treatment-plans';
import {
  resolveExactStaffLocation,
  StaffToPracticeMapping,
} from '../../../exact/destination/mappings/practitioner-to-practice-mapping';
import { PracticeWorksTreatmentPlanBuilder } from './lib/practice-works-treatment-plan-builder';
import { toTextContent, initVersionedSchema } from '@principle-theorem/editor';
import { convertExactAppointmentStatus } from '../../../exact/destination/entities/lib/exact-treatment-plan-builder';
import {
  AppointmentSourceEntity,
  ExactAppointment,
  type IExactAppointment,
} from '../../../exact/source/entities/patient-appointment';
import * as moment from 'moment-timezone';
import {
  PatientSourceEntity,
  PracticeWorksPatient,
} from '../../source/entities/patients';
import { DestinationEntity } from '../../../destination/destination-entity';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { AppointmentCategoryDestination } from '../../../mappings/appointment-to-treatment-category-to-xlsx';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { AppointmentCategoryToTreatmentCategoryMappingHandler } from '../../../exact/destination/mappings/appointment-category-to-treatment-category';
import { AppointmentRoomToPractionerMappingHandler } from '../../../exact/destination/mappings/appointment-room-to-practitioner';
import { ExactItemCodeMappingHandler } from '../../../exact/destination/mappings/item-codes';
import {
  ExactStafferMappingHandler,
  resolveExactStaffer,
} from '../../../exact/destination/mappings/staff';
import { PatientNotesSourceEntity } from '../../../exact/source/entities/patient-notes';
import { PatientTreatmentSourceEntity } from '../../../exact/source/entities/patient-treatments';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { PatientDestinationEntity } from './patient';
import { StafferDestinationEntity } from './staff';
import { PatientTreatmentPlanDestinationEntity } from './patient-treatment-plans';

export const PATIENT_APPOINTMENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_APPOINTMENT_RESOURCE_TYPE,
    label: 'Patient Appointments',
    description: '',
  },
});

export interface IPracticeWorksAppointmentJobData
  extends IPatientAppointmentJobData<PracticeWorksPatient> {
  appointmentRoomToPractitioner: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  appointmentToTreatmentCategory: WithPrimaryKey<
    ITranslationMap<AppointmentCategoryDestination>
  >[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
}

export class PracticeWorksPatientAppointmentsDestinationEntity extends BasePatientAppointmentDestinationEntity<
  PracticeWorksPatient,
  IPracticeWorksAppointmentJobData
> {
  destinationEntity = PATIENT_APPOINTMENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();

  override canMigrateByDateRange = true;
  override batchLimit = 10000;

  override filters = [
    new PatientIdFilter<IPracticeWorksAppointmentJobData>((jobData) =>
      jobData.sourcePatient.data.id.toString()
    ),
  ];

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new AppointmentSourceEntity(),
    notes: new PatientNotesSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
    treatmentPlans: new PatientTreatmentPlanDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    appointmentRoomToPractitioner:
      new AppointmentRoomToPractionerMappingHandler(),
    appointmentToTreatmentCategory:
      new AppointmentCategoryToTreatmentCategoryMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPracticeWorksAppointmentJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practitioners$ = brand$.pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const appointmentRoomToPractitioner$ =
      this.customMappings.appointmentRoomToPractitioner.getRecords(
        translationMap
      );
    const appointmentToTreatmentCategory$ =
      this.customMappings.appointmentToTreatmentCategory.getRecords(
        translationMap
      );
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const staffToPractice$ =
      this.customMappings.staffToPractice.getRecords(translationMap);

    return snapshotCombineLatest([
      brand$,
      staff$,
      practitioners$,
      appointmentRoomToPractitioner$,
      appointmentToTreatmentCategory$,
      sourceItemCodes$,
      staffToPractice$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          patients,
          [
            brand,
            staff,
            practitioners,
            appointmentRoomToPractitioner,
            appointmentToTreatmentCategory,
            sourceItemCodes,
            staffToPractice,
          ],
        ]) =>
          patients.map((sourcePatient) => ({
            sourcePatient,
            brand,
            staff,
            practitioners,
            appointmentRoomToPractitioner,
            appointmentToTreatmentCategory,
            sourceItemCodes,
            staffToPractice,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPracticeWorksAppointmentJobData
  ): Promise<
    | IPatientAppointmentMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
      data.sourcePatient.data
    );

    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId.toString(),
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient,
        `No patient with id ${sourcePatientId}`
      );
    }

    const patient = await Firestore.getDoc(patientRef);

    const sourceAppointments =
      await this.sourceEntities.appointments.filterRecords(
        migration,
        'patientId',
        data.sourcePatient.data.id.toString()
      );

    try {
      const planUid =
        PracticeWorksTreatmentPlanBuilder.getPlanIdentifier(sourcePatientId);
      const defaultPlanName = getDefaultTreatmentPlanName(migration);
      const steps: IStepWithIdentifiers[] = [];

      const appointments: (IPatientAppointmentBuildData | undefined)[] =
        await resolveSequentially(
          sourceAppointments,
          async (sourceAppointment) => {
            const appointmentUid =
              this.sourceEntities.appointments.getSourceRecordId(
                sourceAppointment.data
              );

            const practitioner = await this.resolvePractitioner(
              data,
              translationMap,
              sourceAppointment.data
            );
            if (!practitioner) {
              throw new Error(
                `No practitioner found for id ${sourceAppointment.data.practitioner_initials}`
              );
            }

            const practice = await resolveExactStaffLocation(
              sourceAppointment.data.practitioner_initials,
              translationMap,
              data.staffToPractice,
              migration.configuration.practices
            );
            if (!practice) {
              throw new Error(
                `No practice found for practitioner ${sourceAppointment.data.practitioner_initials}`
              );
            }

            const [appointment, interactions] = await this.buildAppointmentData(
              migration,
              patient,
              sourceAppointment,
              practitioner,
              practice,
              translationMap
            );

            if (!appointment) {
              return;
            }

            const stepUid =
              await PracticeWorksTreatmentPlanBuilder.getStepIdentifier(
                migration,
                this.sourceEntities.appointments,
                sourcePatientId,
                migration.configuration.timezone,
                undefined,
                sourceAppointment,
                sourceAppointments
              );

            const timezone = migration.configuration.timezone;
            const appointmentDate = toMomentTz(
              sourceAppointment.record.translations.appointmentDateTime,
              timezone
            ).format(HISTORY_DATE_FORMAT);

            const categoryDestinationIdentifier =
              data.appointmentToTreatmentCategory.find(
                (categoryMap) =>
                  categoryMap.sourceLabel ===
                  sourceAppointment.data.appointment_category
              )?.associatedValue;

            const categoryDestination = isINamedDocument(
              categoryDestinationIdentifier
            )
              ? await Firestore.getDoc(categoryDestinationIdentifier.ref)
              : undefined;

            const overrideTreatmentCategory = isTag(categoryDestination)
              ? undefined
              : (categoryDestination?.ref as DocumentReference<ITreatmentCategory>);
            if (appointment.treatmentPlan && overrideTreatmentCategory) {
              appointment.treatmentPlan.treatmentStep.display = {
                ...appointment.treatmentPlan.treatmentStep.display,
                overrideTreatmentCategory,
              };
            }

            const appointmentTag = isTag(categoryDestination)
              ? (categoryDestination as INamedDocument<ITag>)
              : undefined;
            if (appointmentTag) {
              appointment.tags.push(appointmentTag);
            }

            if (!appointment.treatmentPlan) {
              steps.push({
                ...TreatmentStep.init({
                  name: sourceAppointment.data.appointment_note?.length
                    ? sourceAppointment.data.appointment_note
                    : `PracticeWorks Appointment - ${appointmentDate}`,
                  status:
                    appointment.status === AppointmentStatus.Complete
                      ? TreatmentStepStatus.Complete
                      : TreatmentStepStatus.Incomplete,
                  schedulingRules: {
                    duration: appointment.event
                      ? Event.duration(appointment.event)
                      : 0,
                  },
                  display: {
                    overrideTreatmentCategory,
                  },
                }),
                sourceIdentifier: stepUid,
                createdAt: appointment.createdAt,
                date: appointmentDate,
              });
            }

            return {
              sourcePatientId: sourcePatientId.toString(),
              patientRef,
              appointmentUid,
              createdAt:
                sourceAppointment.record.translations.appointmentDateTime,
              appointment,
              planUid,
              stepUid,
              interactions,
            };
          }
        );

      return {
        patientRef,
        sourcePatientId: sourcePatientId.toString(),
        appointments: compact(appointments),
        planPairs: [
          {
            plan: {
              ...TreatmentPlan.init({
                name: defaultPlanName,
                status: TreatmentPlanStatus.InProgress,
              }),
              sourceIdentifier: planUid,
            },
            steps: uniqBy(steps, (step) => step.sourceIdentifier),
          },
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  async resolvePractitioner(
    data: IPracticeWorksAppointmentJobData,
    translationMap: TranslationMapHandler,
    sourceAppointment: IExactAppointment
  ): Promise<WithRef<IStaffer> | undefined> {
    if (sourceAppointment.room_id) {
      const appointmentBookPractitionerMap =
        data.appointmentRoomToPractitioner.find(
          (practitionerMap) =>
            practitionerMap.sourceIdentifier === sourceAppointment.room_id
        );

      if (appointmentBookPractitionerMap?.sourceLink?.sourceIdentifier) {
        return resolveExactStaffer(
          appointmentBookPractitionerMap.sourceLink.sourceIdentifier,
          translationMap,
          data.staff
        );
      }
    }

    return resolveExactStaffer(
      sourceAppointment.practitioner_initials,
      translationMap,
      data.staff
    );
  }

  async buildAppointmentData(
    migration: WithRef<IPracticeMigration>,
    patient: WithRef<IPatient>,
    sourceAppointment: IGetRecordResponse<ExactAppointment>,
    practitioner: WithRef<IStaffer>,
    practice: WithRef<IPractice>,
    translationMap: TranslationMapHandler
  ): Promise<
    | [IPatientAppointmentBuildData['appointment'], IInteractionV2[]]
    | [undefined, undefined]
  > {
    // Their table does not contain a created_at date so we will use the appointment date
    const createdAt = sourceAppointment.record.translations.appointmentDateTime;
    const status = convertExactAppointmentStatus(
      sourceAppointment.data.appointment_status
    );
    const interactions: IInteractionV2[] = await this.buildInteractions(
      sourceAppointment,
      migration
    );

    if (sourceAppointment.data.appointment_note) {
      interactions.unshift(
        Interaction.init({
          type: InteractionType.Note,
          title: [toTextContent('Appointment note')],
          content: initVersionedSchema(sourceAppointment.data.appointment_note),
          pinned: true,
          createdAt,
        })
      );
    }

    if (status === AppointmentStatus.Unscheduled) {
      return [undefined, undefined];
    }

    const appointmentEvent = this.getAppointmentEvent(
      sourceAppointment,
      patient,
      practice,
      practitioner
    );
    const isTooFar = toMoment(appointmentEvent.from).isAfter(
      moment().add(5, 'years')
    );

    const associatedPlan = await resolveAssociatedPlan(
      migration,
      this.sourceEntities.appointments,
      sourceAppointment.data.patient_id.toString(),
      practice,
      translationMap,
      sourceAppointment
    );

    const appointment: IPatientAppointmentBuildData['appointment'] = {
      event: isTooFar ? undefined : appointmentEvent,
      eventHistory: [
        {
          event: appointmentEvent,
          createdAt,
        },
      ],
      status: isTooFar ? AppointmentStatus.Unscheduled : status,
      statusHistory: [],
      practice: toNamedDocument(practice),
      practitioner: stafferToNamedDoc(practitioner),
      cancellationHistory: [],
      dependencies: [],
      tags: [],
      treatmentPlan: associatedPlan,
      ...initFirestoreModel(),
    };

    return [appointment, interactions];
  }

  async buildInteractions(
    sourceAppointment: IGetRecordResponse<ExactAppointment>,
    migration: WithRef<IPracticeMigration>
  ): Promise<IInteractionV2[]> {
    const notes = await this.sourceEntities.notes.filterRecords(
      migration,
      'appointmentId',
      sourceAppointment.data.appointment_id
    );
    return notes.map((note) => ({
      ...Interaction.init({
        type: InteractionType.Note,
        title: [toTextContent('Appointment note')],
        content: initVersionedSchema(note.data.note),
        createdAt: note.record.translations.entryDate,
      }),
    }));
  }

  getAppointmentEvent(
    sourceAppointment: IGetRecordResponse<ExactAppointment>,
    patient: WithRef<IPatient>,
    practice: WithRef<IPractice>,
    practitioner: WithRef<IStaffer>
  ): IEvent {
    const from = toMomentTz(
      sourceAppointment.record.translations.appointmentDateTime,
      practice.settings.timezone
    );
    const to = toTimestamp(
      from
        .clone()
        .add(moment.duration(sourceAppointment.data.appointment_duration))
    );
    return Event.init({
      from: toTimestamp(from),
      to,
      practice: toNamedDocument(practice),
      type: EventType.Appointment,
      participants: [
        {
          ...toNamedDocument(patient),
          type: ParticipantType.Patient,
        },
        {
          ...stafferToNamedDoc(practitioner),
          type: ParticipantType.Staffer,
        },
      ],
    });
  }
}

async function resolveAssociatedPlan(
  migration: WithRef<IPracticeMigration>,
  sourceAppointments: AppointmentSourceEntity,
  sourcePatientId: string,
  practice: WithRef<IPractice>,
  translationMap: TranslationMapHandler,
  appointment: IGetRecordResponse<ExactAppointment>
): Promise<IAssociatedTreatment | undefined> {
  const stepUid = await PracticeWorksTreatmentPlanBuilder.getStepIdentifier(
    migration,
    sourceAppointments,
    sourcePatientId,
    practice.settings.timezone,
    undefined,
    appointment
  );

  const stepRef = await findTreatmentStepForAppointment(
    translationMap,
    stepUid
  );
  if (!stepRef) {
    return undefined;
  }
  const step = await Firestore.safeGetDoc(stepRef);
  if (!step) {
    return;
  }

  const plan = await TreatmentStep.treatmentPlan(step);
  return TreatmentPlan.treatmentStepToAssociatedTreatment(plan, step);
}
